import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiLock, FiShield, FiSmartphone, FiKey, FiArrowLeft, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { loadTable } from '../../db-viewer/data/dataAccess';
import apiClient from '../../../lib/api/apiClient';
import { consumeSessionExpiredFlag } from '../../../lib/authGuard';
import './AuthPages.css';

// =============================================
// Auth Flow Steps
// =============================================
type AuthStep =
  | 'email'       // Step 1: enter email
  | 'login'       // Step 2a: existing user — password + 2FA
  | 'verify-2fa'  // Step 2b: enter 2FA code
  | 'new-user'    // Step 2c: new user — will redirect to signup form
  | 'pending'     // Access requested — awaiting owner approval
  | 'rejected';   // Access request was declined

interface AuthPageProps {
  onLogin: (email: string, password: string) => void;
  onNewUser: (email: string, password: string, firstName: string, lastName: string, promoCode?: string) => void;  // create account via /api/auth/signup, then go to About Yourself form
}

interface EmailStatus {
  exists: boolean;
  pending: boolean;
  rejected: boolean;
  needsOnboarding: boolean;
}

/** Check an email's account/approval status — API first, localStorage fallback. */
async function checkEmail(email: string): Promise<EmailStatus> {
  try {
    const response = await apiClient.post<{ exists: boolean; pending?: boolean; rejected?: boolean; needsOnboarding?: boolean }>('/auth/check-email', { email });
    const d = response.data;
    return { exists: !!d.exists, pending: !!d.pending, rejected: !!d.rejected, needsOnboarding: !!d.needsOnboarding };
  } catch {
    // API unreachable — fallback to localStorage (no approval gate offline).
    const users = await loadTable('users');
    const exists = users.some(u => u.email?.toLowerCase() === email.toLowerCase() && u.is_deleted !== 1);
    return { exists, pending: false, rejected: false, needsOnboarding: false };
  }
}

/** Are self-service signups gated by owner approval? Defaults to TRUE (safe) if unknown. */
async function fetchSignupGated(): Promise<boolean> {
  try {
    const response = await apiClient.get<{ signupApprovalRequired: boolean }>('/auth/config');
    return response.data.signupApprovalRequired !== false;
  } catch {
    return true;  // can't reach the server → assume gated (block), never silently open up
  }
}

/** Submit an access request for a new email (gated mode). */
async function requestAccess(email: string): Promise<{ ok: boolean; message: string }> {
  try {
    const response = await apiClient.post<{ message?: string }>('/auth/request-access', { email });
    return { ok: true, message: response.data.message || 'Your request was submitted and is awaiting approval.' };
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.data?.message || 'Could not submit your request. Please try again.';
    return { ok: false, message: msg };
  }
}

/** Basic email format validation */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const AuthPage: React.FC<AuthPageProps> = ({ onLogin, onNewUser }) => {
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaMethod, setTwoFaMethod] = useState<'app' | 'sms' | 'passkey'>('app');
  const [emailError, setEmailError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [twoFaSent, setTwoFaSent] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupError, setSignupError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  // Are self-service signups gated by owner approval? (default true = safe)
  const [signupGated, setSignupGated] = useState(true);

  useEffect(() => { fetchSignupGated().then(setSignupGated); }, []);
  // Set when the app bounced us here because the session expired (inactivity).
  const [sessionExpired] = useState<boolean>(() => consumeSessionExpiredFlag());

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on step change
  useEffect(() => {
    if (step === 'email') emailInputRef.current?.focus();
    if (step === 'login') passwordInputRef.current?.focus();
    if (step === 'verify-2fa') codeInputRef.current?.focus();
  }, [step]);

  // Step 1: Check email
  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }
    if (!isValidEmail(email.trim())) {
      setEmailError('Please enter a valid email format (e.g. you@example.com)');
      return;
    }

    // Check the email's account + approval status (API with localStorage fallback)
    const status = await checkEmail(email.trim());
    if (status.exists) {
      if (status.pending) { setStep('pending'); return; }
      if (status.rejected) { setStep('rejected'); return; }
      // Approved but never onboarded (no real password yet) → complete onboarding.
      if (status.needsOnboarding) { setStep('new-user'); return; }
      setStep('login');
    } else {
      setStep('new-user');
    }
  }, [email, onNewUser]);

  // Step 2a: Login with password
  const handlePasswordSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!password) {
      setLoginError('Please enter your password');
      return;
    }

    // Mock password check (accept any password for now)
    // In real app: API call to verify password hash
    setStep('verify-2fa');
    setTwoFaSent(true);
  }, [password]);

  // Step 2b: Verify 2FA
  const handleTwoFaSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (twoFaMethod === 'passkey') {
      // Mock passkey verification — accept immediately
      onLogin(email.trim(), password);
      return;
    }

    if (!twoFaCode || twoFaCode.length < 6) {
      return;
    }

    // Mock 2FA check (accept any 6-digit code for now)
    onLogin(email.trim(), password);
  }, [twoFaCode, twoFaMethod, email, password, onLogin]);

  // Step 2c: New user — gated → submit an access request; open → onboard directly.
  const handleNewUserContinue = useCallback(async () => {
    if (signupGated) {
      setRequesting(true);
      const res = await requestAccess(email.trim());
      setPendingMessage(res.message);
      setRequesting(false);
      setStep('pending');
      return;
    }
    setSignupError('');
    if (!signupFirstName.trim()) { setSignupError('Please enter your first name'); return; }
    if (signupPassword.length < 8) { setSignupError('Password must be at least 8 characters'); return; }
    if (signupPassword !== signupConfirm) { setSignupError('Passwords do not match'); return; }
    onNewUser(email.trim(), signupPassword, signupFirstName.trim(), signupLastName.trim(), promoCode.trim() || undefined);
  }, [signupGated, email, promoCode, onNewUser, signupFirstName, signupLastName, signupPassword, signupConfirm]);

  const goBack = useCallback(() => {
    setStep('email');
    setPassword('');
    setTwoFaCode('');
    setLoginError('');
    setTwoFaSent(false);
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-container auth-container-centered">
        {/* Left panel — benefits */}
        <div className="auth-left">
          <h1>Akountant</h1>
          <p>The all-in-one platform for document management, bookkeeping, and tax filing.</p>
          <ul className="auth-benefits">
            <li>Secure document management</li>
            <li>Automated bookkeeping</li>
            <li>Multi-jurisdiction tax filing</li>
            <li>Team collaboration tools</li>
            <li>Bank-level security with 2FA</li>
          </ul>
        </div>

        {/* Right panel — auth card */}
        <div className="auth-right">
          <div className="auth-card">

            {/* Session-expired notice (bounced here from a 401) */}
            {sessionExpired && (
              <div className="auth-session-expired" role="status">
                <FiAlertCircle size={16} />
                <span>Your session expired due to inactivity. Please log in again.</span>
              </div>
            )}

            {/* ===== STEP 1: Email Entry ===== */}
            {step === 'email' && (
              <>
                <h2>Log In / Sign Up</h2>
                <p className="auth-step-desc">Enter your email to get started</p>

                {/* Social Login Options */}
                <div className="social-auth">
                  <button className="btn-social btn-google">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32l3.55 2.76c2.07-1.91 3.29-4.74 3.29-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.23 1.06-3.73 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09a6.97 6.97 0 0 1 0-4.18V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l3.56-2.76.01-.55z"/><path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z"/></svg>
                    Continue with Google
                  </button>
                  <button className="btn-social btn-microsoft">
                    <svg width="20" height="20" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                    Continue with Microsoft
                  </button>
                  <button className="btn-social btn-apple">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                    Continue with Apple
                  </button>
                  <button className="btn-social btn-facebook">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Continue with Facebook
                  </button>
                </div>

                <div className="divider">
                  <span>or continue with email</span>
                </div>

                <form onSubmit={handleEmailSubmit}>
                  <div className="form-field">
                    <label htmlFor="auth-email">
                      <FiMail size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Email Address
                    </label>
                    <input
                      ref={emailInputRef}
                      id="auth-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                      autoComplete="email"
                      required
                    />
                    {emailError && (
                      <span className="auth-field-error">
                        <FiAlertCircle size={14} /> {emailError}
                      </span>
                    )}
                  </div>
                  <button type="submit" className="btn-submit">Continue</button>
                </form>

                <p className="auth-terms-note">
                  By continuing, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                </p>
              </>
            )}

            {/* ===== STEP 2a: Existing User — Password ===== */}
            {step === 'login' && (
              <>
                <button className="auth-back-btn" onClick={goBack} type="button">
                  <FiArrowLeft size={16} /> Back
                </button>
                <h2>Welcome Back</h2>
                <p className="auth-step-desc">
                  <FiMail size={14} style={{ marginRight: 4 }} />
                  {email}
                </p>

                <form onSubmit={handlePasswordSubmit}>
                  <div className="form-field">
                    <label htmlFor="auth-password">
                      <FiLock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Password
                    </label>
                    <input
                      ref={passwordInputRef}
                      id="auth-password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setLoginError(''); }}
                      autoComplete="current-password"
                      required
                    />
                    {loginError && (
                      <span className="auth-field-error">
                        <FiAlertCircle size={14} /> {loginError}
                      </span>
                    )}
                  </div>

                  <div className="form-options">
                    <label className="remember-me">
                      <input type="checkbox" />
                      Remember me
                    </label>
                    <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
                  </div>

                  <button type="submit" className="btn-submit">
                    <FiShield size={16} style={{ marginRight: 6 }} />
                    Continue to Verification
                  </button>
                </form>
              </>
            )}

            {/* ===== STEP 2b: 2FA Verification ===== */}
            {step === 'verify-2fa' && (
              <>
                <button className="auth-back-btn" onClick={() => setStep('login')} type="button">
                  <FiArrowLeft size={16} /> Back
                </button>
                <h2>Two-Factor Authentication</h2>
                <p className="auth-step-desc">Choose a verification method</p>

                {/* 2FA Method Selection */}
                <div className="twofa-methods">
                  <button
                    className={`twofa-method-btn ${twoFaMethod === 'app' ? 'active' : ''}`}
                    onClick={() => { setTwoFaMethod('app'); setTwoFaSent(true); }}
                    type="button"
                  >
                    <FiShield size={20} />
                    <span>Authenticator App</span>
                  </button>
                  <button
                    className={`twofa-method-btn ${twoFaMethod === 'sms' ? 'active' : ''}`}
                    onClick={() => { setTwoFaMethod('sms'); setTwoFaSent(true); }}
                    type="button"
                  >
                    <FiSmartphone size={20} />
                    <span>SMS Code</span>
                  </button>
                  <button
                    className={`twofa-method-btn ${twoFaMethod === 'passkey' ? 'active' : ''}`}
                    onClick={() => setTwoFaMethod('passkey')}
                    type="button"
                  >
                    <FiKey size={20} />
                    <span>Passkey</span>
                  </button>
                </div>

                <form onSubmit={handleTwoFaSubmit}>
                  {twoFaMethod === 'passkey' ? (
                    <div className="twofa-passkey-area">
                      <FiKey size={40} className="twofa-passkey-icon" />
                      <p>Use your device's biometric or security key to verify your identity.</p>
                      <button type="submit" className="btn-submit">
                        Verify with Passkey
                      </button>
                      <p className="auth-mock-note">Mock: Click to proceed (no real passkey check)</p>
                    </div>
                  ) : (
                    <>
                      {twoFaSent && (
                        <p className="twofa-sent-msg">
                          <FiCheck size={14} />
                          {twoFaMethod === 'app'
                            ? 'Enter the 6-digit code from your authenticator app'
                            : 'A 6-digit code has been sent to your phone'
                          }
                        </p>
                      )}
                      <div className="form-field">
                        <label htmlFor="auth-2fa-code">Verification Code</label>
                        <input
                          ref={codeInputRef}
                          id="auth-2fa-code"
                          type="text"
                          placeholder="000000"
                          value={twoFaCode}
                          onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="twofa-code-input"
                          autoComplete="one-time-code"
                          required
                        />
                      </div>
                      <button type="submit" className="btn-submit" disabled={twoFaCode.length < 6}>
                        Verify & Log In
                      </button>
                      <p className="auth-mock-note">Mock: Enter any 6 digits (e.g. 123456)</p>
                    </>
                  )}
                </form>
              </>
            )}

            {/* ===== STEP 2c: New User ===== */}
            {step === 'new-user' && (
              <>
                <button className="auth-back-btn" onClick={goBack} type="button">
                  <FiArrowLeft size={16} /> Back
                </button>
                <div className="new-user-welcome">
                  <div className="new-user-icon">{signupGated ? '🔒' : '👋'}</div>
                  <h2>{signupGated ? 'Request access to Akountant' : 'Welcome to Akountant!'}</h2>
                  <p className="auth-step-desc">
                    <strong>{email}</strong> is not registered yet.
                  </p>
                  <p className="new-user-info">
                    {signupGated ? (
                      <>Akountant is currently <strong>private (invitation / approval only)</strong>. Submit a request and the
                      site owner will review it — you'll be able to sign in once it's approved.</>
                    ) : (
                      <>You'll be set up as an <strong>Account Owner</strong> and taken to a quick form
                      to enter your details. You can add family members, businesses, and clients later.</>
                    )}
                  </p>
                  {!signupGated && (
                    <>
                      <div className="new-user-what-you-get">
                        <h4>What you'll get access to:</h4>
                        <ul>
                          <li><FiCheck size={14} /> Account Owner dashboard</li>
                          <li><FiCheck size={14} /> Document Management</li>
                          <li><FiCheck size={14} /> Bookkeeping</li>
                          <li><FiCheck size={14} /> Tax Filing</li>
                          <li><FiCheck size={14} /> Contacts & Calendar</li>
                        </ul>
                      </div>
                      <div className="auth-promo-field">
                        <label htmlFor="signup-first">First name</label>
                        <input id="signup-first" type="text" className="auth-promo-input" placeholder="First name"
                          value={signupFirstName} onChange={e => setSignupFirstName(e.target.value)} />
                      </div>
                      <div className="auth-promo-field">
                        <label htmlFor="signup-last">Last name <span className="auth-promo-optional">(optional)</span></label>
                        <input id="signup-last" type="text" className="auth-promo-input" placeholder="Last name"
                          value={signupLastName} onChange={e => setSignupLastName(e.target.value)} />
                      </div>
                      <div className="auth-promo-field">
                        <label htmlFor="signup-pw">Create a password</label>
                        <input id="signup-pw" type="password" className="auth-promo-input" placeholder="At least 8 characters"
                          value={signupPassword} onChange={e => setSignupPassword(e.target.value)} />
                      </div>
                      <div className="auth-promo-field">
                        <label htmlFor="signup-pw2">Confirm password</label>
                        <input id="signup-pw2" type="password" className="auth-promo-input" placeholder="Re-enter password"
                          value={signupConfirm} onChange={e => setSignupConfirm(e.target.value)} />
                      </div>
                      {signupError && <p className="auth-error" style={{ color: '#dc2626', margin: '4px 0' }}>{signupError}</p>}
                      <div className="auth-promo-field">
                        <label htmlFor="auth-promo">Have a promo code? <span className="auth-promo-optional">(optional)</span></label>
                        <input
                          id="auth-promo"
                          type="text"
                          className="auth-promo-input"
                          placeholder="Enter code"
                          value={promoCode}
                          onChange={e => setPromoCode(e.target.value)}
                          autoCapitalize="characters"
                        />
                      </div>
                    </>
                  )}
                  <button className="btn-submit" onClick={handleNewUserContinue} disabled={requesting}>
                    {requesting ? 'Submitting…' : (signupGated ? 'Request Access' : 'Set Up My Account')}
                  </button>
                </div>
              </>
            )}

            {/* ===== Access requested — awaiting approval ===== */}
            {step === 'pending' && (
              <>
                <button className="auth-back-btn" onClick={goBack} type="button">
                  <FiArrowLeft size={16} /> Back
                </button>
                <div className="new-user-welcome">
                  <div className="new-user-icon">⏳</div>
                  <h2>Request submitted</h2>
                  <p className="new-user-info">
                    {pendingMessage || <>Your access request for <strong>{email}</strong> is awaiting approval by the site owner.
                    You'll be able to sign in once it's approved.</>}
                  </p>
                </div>
              </>
            )}

            {/* ===== Access request declined ===== */}
            {step === 'rejected' && (
              <>
                <button className="auth-back-btn" onClick={goBack} type="button">
                  <FiArrowLeft size={16} /> Back
                </button>
                <div className="new-user-welcome">
                  <div className="new-user-icon">🚫</div>
                  <h2>Access not approved</h2>
                  <p className="new-user-info">
                    The access request for <strong>{email}</strong> was not approved. Please contact the site owner if you think this is a mistake.
                  </p>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
